import { CarModelFinishingLevelDto, CarModelFinishingLevelEngineDto } from "api"
import { useIsDesktop } from "common/MediaQueries"
import { trackPopUpOpen } from "common/analytics"
import { useFormik } from "formik"
import { useDebounce } from "hooks/useDebounce"
import { ceil, round } from "lodash"
import dynamic from "next/dynamic"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import PopUp from "reusable/popups/PopUp"
import FinanceSlider from "reusable/sliders/FinanceSlider"
import { useTypedSelector } from "store"
import styled from "styled-components"
import { getCarmodelCalculator } from "../../api/generated/MazdaApi"
import BottomActions from "./BottomActions"
import LoanDetails from "./LoanDetails"
import TopModelDetails from "./TopModelDetails"
import { calculate, calculateLoanAmount } from "./utils"

const CopyPopup = dynamic(() => import("./CopyPopup"), { ssr: false })
const TermsPopup = dynamic(() => import("./TermsPopup"), { ssr: false })
const ContactPopup = dynamic(() => import("reusable/ContactPopup"), {
  ssr: false,
})

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const prepayPrecent = 0.3
const baloonPrecent = 0.5

type Props = {
  modelName: string
  modelId: number
  popupOpen: boolean
  setPopupOpen: Dispatch<SetStateAction<boolean>>
  onlyClick?: boolean
  isPurchaseFlow?: boolean
}

export type FormikInitialValues = {
  defaultMonthlyPayment: number
  defaultPrePay: number
  interest: number
  primeInterest: number
  months: number
  endOfPeriodPayment: number
  primeMargin: number
}

export type SelectFormikInitialValues = {
  engineId: number
  finishingLevel: string
}

export type InterestType = {
  value: number
}

export type KeysType = {
  [key: number]: number
}

const Finance = ({
  modelName,
  modelId,
  popupOpen,
  setPopupOpen,
  onlyClick,
}: Props) => {
  const debounce = useDebounce(1000)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isDesktop = useIsDesktop()

  //Need those 3 properties for the configurator page
  const { selectedColorCode } = useTypedSelector((state) => state.color)
  const { selectedUpholsteryCode } = useTypedSelector(
    (state) => state.upholstery
  )
  const { selectedDetailingCode } = useTypedSelector((state) => state.detailing)

  const state = useTypedSelector((state) => state)

  const [termsPopupOpen, setTermsPopupOpen] = useState<boolean>(false)
  const [copyPopupOpen, setCopyPopupOpen] = useState<boolean>(false)
  const [contactPopupOpen, setContactPopupOpen] = useState<boolean>(false)

  const [engines, setEngines] = useState<CarModelFinishingLevelEngineDto[]>([])
  const [finishingLevels, setFinishingLevels] = useState<
    CarModelFinishingLevelDto[]
  >([])

  const [selectedEngineId, setSelectedEngineId] = useState<number | null>(null)

  const shopEngines = state.detailing.engines.map((item) =>
    Object.assign({}, item, { engineName: item.engineIdName })
  )

  const [initialValues] = useState<FormikInitialValues>({
    defaultMonthlyPayment: 0,
    defaultPrePay: 0,
    interest: 0,
    primeInterest: 0,
    months: 60,
    endOfPeriodPayment: 0,
    primeMargin: 0,
  })
  //formik for finance calculator
  const formik = useFormik({
    initialValues,
    onSubmit: () => {},
  })

  const [selectInitialValues] = useState<SelectFormikInitialValues>({
    engineId: 0,
    finishingLevel: "",
  })
  //formik for saving dropdown state
  const selectFormik = useFormik({
    initialValues: selectInitialValues,
    onSubmit: () => {},
  })

  // ✅ Utility to get and set query parameters with next/navigation
  const getQueryObj = useCallback(() => {
    const obj: Record<string, string> = {}
    searchParams.forEach((v, k) => (obj[k] = v))
    return obj
  }, [searchParams])

  //Changing query on any property change
  const changeQuery = useCallback(() => {
    const queryParams = {
      ...getQueryObj(),
      defaultMonthlyPayment: String(formik.values.defaultMonthlyPayment),
      defaultPrePay: String(formik.values.defaultPrePay),
      months: String(formik.values.months),
      endOfPeriodPayment: String(formik.values.endOfPeriodPayment),
      finishingLevel: selectFormik.values.finishingLevel,
      engineId: String(selectFormik.values.engineId),
      color: selectedColorCode || "",
      upholstery: selectedUpholsteryCode || "",
      detailing: selectedDetailingCode || "",
      loaded: "true",
    }

    const queryString = new URLSearchParams(queryParams).toString()
    router.replace(`${pathname}?${queryString}`, { scroll: false })
  }, [
    formik.values,
    selectFormik.values,
    selectedColorCode,
    selectedUpholsteryCode,
    selectedDetailingCode,
    pathname,
    router,
    getQueryObj,
  ])

  // Returns true if you're in the configurator / shop
  const isshop = () => pathname.includes("shop")

  useEffect(() => {
    //If the popup is open fetch and set data
    getCarmodelCalculator(modelId).then((res) => {
      setEngines(res?.data?.body?.engines)
      if (
        getQueryObj().engineId &&
        getQueryObj().finishingLevel &&
        !getQueryObj().defaultMonthlyPayment
      ) {
        selectFormik.setFieldValue(
          "engineId",
          res?.data?.body?.engines.find(
            (item) => item.engineId === Number(getQueryObj().engineId)
          )?.engineId
        )
      } else {
        if (isshop()) {
          const engineId = shopEngines.find((engine) =>
            engine.finishingLevels.some(
              (detailing) => detailing.details.code === selectedDetailingCode
            )
          )?.engineId

          setSelectedEngineId(engineId ?? null)

          selectFormik.setFieldValue("engineId", engineId)
          return
        }

        selectFormik.setFieldValue(
          "engineId",
          res?.data?.body?.engines.find(
            (item) =>
              item.engineId === selectFormik.values.engineId ||
              item.engineId === Number(getQueryObj().engineId)
          )?.engineId || res?.data?.body?.engines[0].engineId
        )
      }
    })
    // If user redirects from outside page and wants to open popup automatically
    if (getQueryObj()?.openPopup) {
      setPopupOpen(true)
    }
  }, [getQueryObj()?.engineId, selectedDetailingCode])

  useEffect(() => {
    //If the engines are set find and set finishingLevel that is selected outside of the component or default
    if (engines && engines.length) {
      if (isshop() && !selectedEngineId) return

      const selectedFinish = isshop()
        ? engines.find((item) => item.engineId === selectedEngineId)
            ?.finishingLevels
        : engines.find((item) => item.engineId === selectFormik.values.engineId)
            ?.finishingLevels

      setFinishingLevels(selectedFinish ?? [])
      if (
        getQueryObj().engineId &&
        getQueryObj().finishingLevel &&
        !getQueryObj().defaultMonthlyPayment
      ) {
        selectFormik.setFieldValue(
          "finishingLevel",
          selectedFinish?.find(
            (item) =>
              item.name === getQueryObj().finishingLevel ||
              item.code === getQueryObj().finishingLevel
          )?.name
        )
      } else {
        if (isshop()) {
          selectFormik.setFieldValue(
            "finishingLevel",
            selectedFinish?.find((item) => item.code === selectedDetailingCode)
              ?.name
          )
          return
        }

        selectFormik.setFieldValue(
          "finishingLevel",
          selectedFinish?.find(
            (item) =>
              item.name === getQueryObj().finishingLevel ||
              item.code === getQueryObj().finishingLevel
          )?.name || selectedFinish?.[0].name
        )
      }
    }
  }, [
    selectFormik.values.engineId,
    engines,
    getQueryObj().finishingLevel,
    selectedDetailingCode,
  ])
  const [maxMonthly, setMaxMonthly] = useState<number>(0)
  const [minMonthly, setMinMonthly] = useState<number>(0)
  useEffect(() => {
    //If popup opens or formik values change call changequery and set query values
    if (formik.values && popupOpen) {
      debounce(() => {
        changeQuery()
        return {}
      })
    }
  }, [
    formik.values.defaultMonthlyPayment,
    formik.values.defaultPrePay,
    formik.values.months,
    formik.values.endOfPeriodPayment,
    selectFormik.values.finishingLevel,
    selectFormik.values.engineId,
  ])

  //Selected finishing level
  const selected = useMemo(() => {
    return (
      finishingLevels.find(
        (item) => item.name === selectFormik.values.finishingLevel
      ) || ({} as CarModelFinishingLevelDto)
    )
  }, [finishingLevels, selectFormik.values.finishingLevel])

  //If interest values are set and finishing level is selected calculate different options of values for selected months
  useEffect(() => {
    if (formik.values.primeInterest && formik.values.primeMargin && selected) {
      const loanPercentage =
        formik.values.primeMargin + formik.values.primeInterest

      //Calculate maximum monthly payment
      const maximumMonthly = calculate(
        selected.price,
        loanPercentage,
        formik.values.months
      )
      const minimumMonthly =
        calculate(
          20000 - formik.values.endOfPeriodPayment,
          loanPercentage,
          formik.values.months
        ) +
        formik.values.endOfPeriodPayment * (loanPercentage / 12 / 100)
      setMaxMonthly(maximumMonthly)
      setMinMonthly(minimumMonthly)
    }
  }, [
    formik.values.endOfPeriodPayment,
    formik.values.months,
    formik.values.primeInterest,
    formik.values.primeMargin,
    selected,
  ])

  // If popup isn't loaded and url comes with defaultMonthlyPayment set initial values as it comes.
  const handleInitialQueryData = useCallback(() => {
    if (
      !getQueryObj().loaded &&
      getQueryObj().defaultMonthlyPayment &&
      !onlyClick
    ) {
      formik.setValues({
        ...formik.values,
        defaultMonthlyPayment: Number(getQueryObj()?.defaultMonthlyPayment),
        defaultPrePay: Number(getQueryObj()?.defaultPrePay),
        months: Number(getQueryObj()?.months),
        endOfPeriodPayment: Number(getQueryObj()?.endOfPeriodPayment),
        primeInterest: selected?.calculator?.primeInterest,
        primeMargin: selected?.calculator?.primeMargin,
      })
      const newParams = new URLSearchParams(searchParams.toString())
      newParams.set("loaded", "true")

      router.replace(`${pathname}?${newParams.toString()}`)
      setPopupOpen(true)
    }
  }, [
    selected.calculator,
    onlyClick,
    getQueryObj()?.defaultMonthlyPayment,
    getQueryObj()?.defaultPrePay,
    getQueryObj()?.endOfPeriodPayment,
    getQueryObj().loaded,
    getQueryObj()?.months,
    router,
    setPopupOpen,
  ])

  const handleChange = useCallback(
    (val: number, id: string) => {
      const loanPercentage =
        formik.values.primeMargin + formik.values.primeInterest

      switch (id) {
        case "defaultPrePay": {
          const loanTotal =
            selected.price -
            (formik.values.endOfPeriodPayment ?? 0) -
            (val ?? 0)

          formik.setValues({
            ...formik.values,
            [id]: Number(val),
            defaultMonthlyPayment: ceil(
              calculate(loanTotal, loanPercentage, formik.values.months) +
                formik.values.endOfPeriodPayment * (loanPercentage / 12 / 100)
            ),
            endOfPeriodPayment:
              selected.price - Number(val) <= formik.values.endOfPeriodPayment
                ? selected.price - Number(val) - 2000 <= 20000
                  ? 18000
                  : selected.price - Number(val) - 2000
                : formik.values.endOfPeriodPayment,
          })

          break
        }
        case "endOfPeriodPayment": {
          const loanTotal =
            selected.price - Number(val) - (formik.values.defaultPrePay ?? 0)

          formik.setValues({
            ...formik.values,
            [id]: Number(val),
            defaultMonthlyPayment: ceil(
              calculate(loanTotal, loanPercentage, formik.values.months) +
                Number(val) * (loanPercentage / 12 / 100)
            ),
          })

          break
        }
        case "months": {
          const loanTotal =
            selected.price -
            (formik.values.endOfPeriodPayment ?? 0) -
            (formik.values.defaultPrePay ?? 0)
          formik.setValues({
            ...formik.values,
            [id]: Number(val),
            defaultMonthlyPayment: ceil(
              calculate(loanTotal, loanPercentage, Number(val)) +
                formik.values.endOfPeriodPayment * (loanPercentage / 12 / 100)
            ),
          })

          break
        }
        case "defaultMonthlyPayment": {
          const loanTotal =
            selected.price - (formik.values.endOfPeriodPayment ?? 0)

          const presentVal = calculateLoanAmount(
            val,
            loanPercentage,
            formik.values.months / 12
          )
          const prePay = loanTotal - round(presentVal, -2)

          if (!(val >= maxMonthly || val <= minMonthly)) {
            formik.setValues({
              ...formik.values,
              [id]: Number(val),
              defaultPrePay: prePay <= 0 ? 0 : prePay,
              endOfPeriodPayment:
                prePay <= 0 ? 0 : formik.values.endOfPeriodPayment,
            })
          } else if (val >= maxMonthly && formik.values[id] !== maxMonthly) {
            formik.setValues({
              ...formik.values,
              [id]: Number(maxMonthly),
              defaultPrePay: prePay <= 0 ? 0 : prePay,
              endOfPeriodPayment:
                prePay <= 0 ? 0 : formik.values.endOfPeriodPayment,
            })
          } else if (val <= minMonthly && formik.values[id] !== minMonthly) {
            formik.setValues({
              ...formik.values,
              [id]: Number(minMonthly),
              defaultPrePay: prePay <= 0 ? 0 : prePay,
              endOfPeriodPayment:
                prePay <= 0 ? 0 : formik.values.endOfPeriodPayment,
            })
          }

          break
        }
      }
    },
    [
      formik.values.defaultPrePay,
      formik.values.defaultMonthlyPayment,
      formik.values.endOfPeriodPayment,
      formik.values.months,
      finishingLevels,
      maxMonthly,
      minMonthly,
      selectFormik.values.finishingLevel,
      selected.price,
    ]
  )

  useEffect(() => {
    if (selected?.calculator?.defaultMonthlyPayment) {
      const loanTotal = selected.price - (formik.values.endOfPeriodPayment ?? 0)

      const presentVal = calculateLoanAmount(
        selected?.calculator?.defaultMonthlyPayment,
        selected?.calculator?.primeInterest + selected?.calculator?.primeMargin,
        formik.values.months / 12
      )
      const prePay = loanTotal - ceil(presentVal)
      const modifiedCalculator = {
        ...selected.calculator,
        defaultMonthlyPayment: selected?.calculator?.defaultMonthlyPayment ?? 0,
        defaultPrePay: prePay,
      }
      formik.setValues({ ...formik.values, ...modifiedCalculator })
    } else {
      const prepayByPrice = prepayPrecent
        ? selected.price * prepayPrecent
        : selected?.calculator?.defaultPrePay
      const baloonByPrice = baloonPrecent ? selected.price * baloonPrecent : 0
      const loanTotal = selected.price - prepayByPrice - baloonByPrice

      const modifiedCalculator = {
        ...selected.calculator,
        defaultMonthlyPayment: ceil(
          calculate(
            loanTotal,
            selected?.calculator?.primeInterest +
              selected?.calculator?.primeMargin,
            36
          )
        ),
        defaultPrePay: prepayByPrice,
        endOfPeriodPayment: baloonByPrice,
      }
      formik.setValues({ ...formik.values, ...modifiedCalculator })
    }
  }, [selectFormik.values.finishingLevel, selectFormik.values.engineId])

  useEffect(() => {
    if (
      selected &&
      getQueryObj()?.openPopup &&
      getQueryObj()?.defaultMonthlyPayment
    ) {
      // If user goes from popup to summary
      handleInitialQueryData()
      const newParams = new URLSearchParams(searchParams.toString())
      newParams.set("loaded", "true")

      router.replace(`${pathname}?${newParams.toString()}`)
    }
  }, [])

  const handleCopyOpen = useCallback(() => {
    if (isDesktop) {
      setPopupOpen(false)
      setCopyPopupOpen(true)
      changeQuery()
    } else {
      changeQuery()
      navigator?.share({ text: copyText, url: link })
    }
  }, [changeQuery, setPopupOpen])

  const handleTermsOpen = useCallback(() => {
    setPopupOpen(false)
    setTermsPopupOpen(true)
  }, [setPopupOpen])

  const handleContactOpen = useCallback(() => {
    setPopupOpen(false)
    setContactPopupOpen(true)
    trackPopUpOpen("Finance suggestion")
  }, [setPopupOpen])
  const copyText = `${modelName} ${selectFormik.values.finishingLevel}\n בהצעת מימון בהתאמה אישית`
  const link = useMemo(() => {
    const params = new URLSearchParams(searchParams.toString())
    params.set("openPopup", "true")
    params.delete("loaded")
    return `${process.env.ORIGIN}${pathname}?${params.toString()}`
  }, [pathname, searchParams])

  const constumClose = useCallback(() => {
    //Temporarily removed needs testing, this makes model page jump to top.
    // if (!router.asPath.includes("shop")) {
    //   router.replace(router.asPath.split("?")[0]) + window.location.hash
    // }
  }, [router])
  const description = `שם דגם: ${modelName}\n
  חבילת אבזור: ${selectFormik.values.finishingLevel}
  גובה מקדמה: ${formik.values.defaultPrePay}
  גובה החזר סוף תקופה: ${formik.values.endOfPeriodPayment}
  גובה החזר חודשי: ${formik.values.defaultMonthlyPayment}
  מספר תשלומים: ${formik.values.months}
 `

  const bottomData = useMemo(
    () => [
      {
        label: "תשלום חודשי",
        value: formik.values.defaultMonthlyPayment,
        withShekel: true,
      },
      {
        label: "תקופת החזר",
        value: formik.values.months,
        withShekel: false,
      },
      {
        label: "מקדמה בתחילת תקופה",
        value: formik.values.defaultPrePay,
        withShekel: true,
      },
      {
        label: "תשלום סוף תקופה",
        value: formik.values.endOfPeriodPayment,
        withShekel: true,
      },
    ],
    [
      formik.values.defaultMonthlyPayment,
      formik.values.defaultPrePay,
      formik.values.endOfPeriodPayment,
      formik.values.months,
    ]
  )

  return (
    <>
      <ContactPopup
        modelId={modelId}
        description={description}
        popupOpen={contactPopupOpen}
        setPopupOpen={setContactPopupOpen}
        customClose={() => {
          setPopupOpen(true)
          setContactPopupOpen(false)
        }}
      />

      <CopyPopup
        link={link}
        copyText={copyText}
        buttonText="העתקת לינק לשיתוף"
        setCopyPopupOpen={setCopyPopupOpen}
        copyPopupOpen={copyPopupOpen}
        customClose={() => {
          setCopyPopupOpen(false)
          setPopupOpen(true)
        }}
      />

      <TermsPopup
        setTermsPopupOpen={setTermsPopupOpen}
        termsPopupOpen={termsPopupOpen}
        customClose={() => {
          setTermsPopupOpen(false)
          setPopupOpen(true)
        }}
      />

      <PopUp
        header={"מחשבון מימון"}
        subHeader={`במחשבון זה אפשר לבצע סימולציה למסלול המימון המתאים לרכישת המאזדה שלך.
        יש לבחור את נפח המנוע ואת רמת הגימור, ואז להתאים הצעת מימון גמישה ונוחה.
        בסיום הבדיקה אפשר להשאיר פרטים, ונציג שלנו ייצור קשר להמשך ייעוץ אישי.
       <br/>  גובה הריבית עשוי להשתנות מעת לעת.`}
        setOpen={setPopupOpen}
        open={popupOpen}
        maxWidth="md"
        width={isDesktop ? 750 : "100%"}
        customClose={() => {
          setPopupOpen(false)
          constumClose()
        }}
      >
        <StyledContainer>
          <TopModelDetails
            modelName={modelName}
            formik={selectFormik}
            engines={engines}
            finishingLevels={finishingLevels}
            selected={selected}
          />

          <FinanceSlider
            low={minMonthly}
            high={maxMonthly}
            shekel={true}
            label="החזר חודשי"
            formik={formik}
            step={100}
            id="defaultMonthlyPayment"
            handleChange={handleChange}
          />

          <FinanceSlider
            disabled={false}
            low={24}
            high={60}
            shekel={false}
            label="תקופה"
            formik={formik}
            id="months"
            handleChange={handleChange}
          />
          <FinanceSlider
            low={0}
            high={selected.price - 20000}
            shekel={true}
            label="מקדמה"
            formik={formik}
            disabled={
              selected.price - formik.values.defaultMonthlyPayment === 0
            }
            id="defaultPrePay"
            handleChange={handleChange}
          />
          <FinanceSlider
            low={0}
            high={ceil(
              selected.price / 2 > selected.price - formik.values.defaultPrePay
                ? selected.price - formik.values.defaultPrePay - 2000
                : selected.price / 2
            )}
            secondHigh={selected.price / 2}
            shekel={true}
            label="תשלום סוף תקופה (בלון)"
            formik={formik}
            id="endOfPeriodPayment"
            handleChange={handleChange}
            optional={true}
            toolTipText={`מסלול מימון המשלב תשלום מקדמה, פריסת תשלומים חודשית ותשלום היתרה בסוף התקופה או בחינת יצירת עסקת טרייד אין להחלפת הרכב ברכב חדש.`}
          />
          <LoanDetails data={bottomData} />
          <BottomActions
            onCopyOpen={handleCopyOpen}
            onTermsOpen={handleTermsOpen}
            onContactOpen={handleContactOpen}
          />
        </StyledContainer>
      </PopUp>
    </>
  )
}

export default Finance
